"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { flushActiveCaptures } from "@/components/forms/renderer/SubmissionMediaContext";
import { flushDraftWrites } from "@/services/draftService";
import { toast } from "sonner";
import { delegateAnsweringBack } from "@/services/answeringLeaveGuard";
import { OPERATIONAL_HOME, detailBackHref } from "@/lib/operationalRoutes";

function getWorkspaceBackHref(url: URL): string | null {
  const path = url.pathname.replace(/\/+$/, "");
  const home = OPERATIONAL_HOME;
  if (path === home) return null;
  // As demais telas da barra operacional voltam para o Início.
  if (["/secure/schedule", "/secure/tasks", "/secure/analysis", "/secure/events"].includes(path)) return home;

  // Match the page Back destinations and retain only the parent's context.
  if (path === "/secure/tasks/details/view" || path === "/secure/tasks/details/submission") {
    return detailBackHref(url.searchParams);
  }
  if (path === "/secure/resources/new" || path === "/secure/resources/edit") {
    const group = url.searchParams.get("group");
    return group ? `/secure/resources?${new URLSearchParams({ group })}` : "/secure/resources-group";
  }
  if (path === "/secure/resources") return "/secure/resources-group";

  const parent = path.slice(0, path.lastIndexOf("/"));
  const parentPages = [
    "/secure/tasks",
    "/secure/forms",
    "/secure/resources-group",
    "/secure/users-group",
    "/secure/workflows",
    "/secure/account",
  ];
  return parentPages.includes(parent) ? parent : home;
}

export function CapacitorBackButton() {
  const router = useRouter();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let navigating = false;
    const listener = App.addListener("backButton", () => {
      if (delegateAnsweringBack()) return;
      if (navigating) return;
      navigating = true;
      void (async () => {
        const url = new URL(window.location.href);
        const path = url.pathname.replace(/\/+$/, "") || "/";
        const inWorkspace = path === "/secure" || path.startsWith("/secure/");
        const destination = inWorkspace ? getWorkspaceBackHref(url) : null;
        if (inWorkspace && !destination) return;

        await flushActiveCaptures();
        await flushDraftWrites();
        if (window.location.href !== url.href) return;

        if (destination) {
          // History can contain login, workspace selection, or another workspace.
          router.push(destination);
        } else if (path === "/" || path === "/login") {
          await App.exitApp();
        } else {
          router.back();
        }
      })().catch(error => toast.error(String(error))).finally(() => { navigating = false; });
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [router]);

  return null;
}
